import { LoadingOutlined } from "@ant-design/icons";
import { Bar } from "@nivo/bar";
import Spin from "antd/lib/spin";
import theme from "antd/lib/theme";
import { useTranslation } from "react-i18next";
import { ClassroomRepartitionLegend } from "src/features/dashboards/components";

import {
    IClassroomItem,
    IClassroomLearnersProgressionQueryResult,
    useClassroomLearnersProgressionQuery,
} from "@sol/sdk";

export enum Status {
    UNASSIGNED = "unassigned",
    ASSIGNED = "assigned",
    CORRECTED = "corrected",
}

type CompetenceDataType = {
    title: Status;
    percentage: number; // Pourcentage (0-100)
    count: number; // Valeur absolue pour référence
    total: number; // Diviseur (apprenants * compétences)
};

export type classroomRepartitionDataType = {
    imitate: CompetenceDataType[];
    adapt: CompetenceDataType[];
    transpose: CompetenceDataType[];
};

const defaultData: classroomRepartitionDataType = {
    imitate: [
        { title: Status.UNASSIGNED, percentage: 100, count: 1, total: 1 },
        { title: Status.ASSIGNED, percentage: 0, count: 0, total: 0 },
        { title: Status.CORRECTED, percentage: 0, count: 0, total: 0 },
    ],
    adapt: [
        { title: Status.UNASSIGNED, percentage: 100, count: 1, total: 1 },
        { title: Status.ASSIGNED, percentage: 0, count: 0, total: 0 },
        { title: Status.CORRECTED, percentage: 0, count: 0, total: 0 },
    ],
    transpose: [
        { title: Status.UNASSIGNED, percentage: 100, count: 1, total: 1 },
        { title: Status.ASSIGNED, percentage: 0, count: 0, total: 0 },
        { title: Status.CORRECTED, percentage: 0, count: 0, total: 0 },
    ],
};

type ClassroomRepartitionProps = {
    className?: string;
    classroom: IClassroomItem;
};

const toCategory = (level: number): keyof classroomRepartitionDataType => {
    switch (level) {
        case 1:
            return "imitate";
        case 2:
            return "adapt";
        case 3:
            return "transpose";
        default:
            throw new Error(`Invalid skill level: ${level}`);
    }
};

const toStatus = (status: string): Status => {
    switch (status) {
        case "unassigned":
            return Status.UNASSIGNED;
        case "assigned":
            return Status.ASSIGNED;
        default:
            return Status.CORRECTED; // acquired, but not only...
    }
};

export const transformLearnersProgressionToRepartition = (
    apiData: IClassroomLearnersProgressionQueryResult | null | undefined,
): classroomRepartitionDataType => {
    if (!apiData?.["hydra:member"]) {
        return defaultData;
    }

    const learners = apiData["hydra:member"].flatMap(a => a); // endpoint provides data into an Array

    // (users * skills)
    const totalBase = learners.reduce(
        (acc, learner) =>
            acc + learner.frameworks.reduce((fwAcc, framework) => fwAcc + (framework?.skills?.length || 0), 0),
        0,
    );

    // UNASSIGNED must be 100% in case of empty data (no learner or no skill)
    if (totalBase === 0) {
        return defaultData;
    }

    const repartition: classroomRepartitionDataType = {
        imitate: [
            { title: Status.UNASSIGNED, percentage: 0, count: 0, total: totalBase },
            { title: Status.ASSIGNED, percentage: 0, count: 0, total: totalBase },
            { title: Status.CORRECTED, percentage: 0, count: 0, total: totalBase },
        ],
        adapt: [
            { title: Status.UNASSIGNED, percentage: 0, count: 0, total: totalBase },
            { title: Status.ASSIGNED, percentage: 0, count: 0, total: totalBase },
            { title: Status.CORRECTED, percentage: 0, count: 0, total: totalBase },
        ],
        transpose: [
            { title: Status.UNASSIGNED, percentage: 0, count: 0, total: totalBase },
            { title: Status.ASSIGNED, percentage: 0, count: 0, total: totalBase },
            { title: Status.CORRECTED, percentage: 0, count: 0, total: totalBase },
        ],
    };

    learners.forEach((learner: any) => {
        if (!learner?.frameworks) {
            return;
        }

        learner.frameworks.forEach((framework: any) => {
            framework?.skills?.forEach((skill: any) => {
                [1, 2, 3].forEach(level => {
                    const levelKey = `skillLevel${level}` as keyof typeof skill;
                    const category = toCategory(level);
                    const status = toStatus(skill[levelKey]);

                    const statusIndex = repartition[category].findIndex(item => item.title === status);
                    if (statusIndex !== -1) {
                        repartition[category][statusIndex].count++;
                    }
                });
            });
        });
    });

    Object.keys(repartition).forEach(category => {
        const cat = category as keyof classroomRepartitionDataType;
        repartition[cat].forEach(item => {
            if (item.total > 0) {
                item.percentage = Math.min(Math.round((item.count / item.total) * 100), 100);
            }
        });
    });

    return repartition;
};

/*
        Define the color according to the status
    */
export const getColorByStatus = (status: Status, token: ReturnType<typeof theme.useToken>["token"]): string => {
    switch (status) {
        case Status.UNASSIGNED:
            return token.colorBorder + "DD";
        case Status.ASSIGNED:
            return token.colorPrimary + "80"; // 50% d'opacité (alpha channel: 99 hex = 153/255)
        case Status.CORRECTED:
            return token.colorPrimary;
        default:
            return token.colorBorder;
    }
};

export function ClassroomRepartition({ className, classroom }: ClassroomRepartitionProps) {
    const [t] = useTranslation();
    const { token: antToken } = theme.useToken();

    /*
        To improve the readability of the graph, we add a light grey bar
        to fill the empty space, so that the graph is always visible,
        even if the percentage is ZERO.
    */
    const addPlaceholder = (data: CompetenceDataType[]) => {
        return data.map(item => ({
            ...item,
            percentage: item.percentage,
            remaining: 100 - item.percentage,
        }));
    };

    /*
        By default, the tick is marked with titles (assigned, submitted, corrected),
        but in our case, we want the bar value to be displayed instead.
    */
    const customTick = (value: string, x: number) => {
        return (
            <g transform={`translate(${x}, 8)`}>
                <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ fontSize: "11px", fill: "#666", fontWeight: "500" }}
                >
                    {value}%
                </text>
            </g>
        );
    };

    const { data: apiData, isLoading } = useClassroomLearnersProgressionQuery(
        { classroom },
        {
            enabled: !!classroom,
        },
    );

    const repartitions = transformLearnersProgressionToRepartition(apiData);

    return (
        <div className={className}>
            <h3 className="text-gray-800 mb-2 text-lg font-semibold">
                {t("component.dashboard.ClassroomRepartition.title")}
            </h3>

            {isLoading && (
                <div className="flex h-16 items-center justify-center">
                    <Spin indicator={<LoadingOutlined spin />} size="large" />
                </div>
            )}

            {!isLoading && repartitions && (
                <>
                    <div className="flex flex-col flex-wrap xs:flex-row">
                        {Object.keys(repartitions).map((key: keyof classroomRepartitionDataType, index: number) => (
                            <div key={key} className="flex flex-1 justify-start sm:justify-center">
                                <Bar
                                    data={addPlaceholder(repartitions[key])}
                                    indexBy="title"
                                    keys={["percentage", "remaining"]}
                                    width={88}
                                    height={90}
                                    enableGridX={false}
                                    enableGridY={false}
                                    enableLabel={false}
                                    padding={0.45}
                                    margin={{ bottom: 35 }}
                                    colors={({ id, data }) =>
                                        id === "remaining"
                                            ? antToken.colorBorder + 40
                                            : getColorByStatus(data.title as Status, antToken)
                                    }
                                    borderRadius={3}
                                    maxValue={100}
                                    axisBottom={{
                                        legendPosition: "middle",
                                        legendOffset: 26,
                                        legend:
                                            `${index + 1}. ` +
                                            t(`page.classrooms.home.learners.skills.${key}`).toLowerCase(),
                                        renderTick: ({ value, x }) => {
                                            const label = repartitions[key].find(item => item.title === value);
                                            return customTick(String(label?.percentage || 0), x);
                                        },
                                    }}
                                    axisLeft={null}
                                    isInteractive={false}
                                />
                            </div>
                        ))}
                    </div>

                    <ClassroomRepartitionLegend />
                </>
            )}
        </div>
    );
}
